import React, { useState } from 'react'
import { Button, Badge, Col, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Box, Tag, Trash2 } from 'lucide-react'
import styled from 'styled-components'
import { Model } from '../../../types/models/model'
import StarRating from '../../StarRating'
import ButtonDeployModel from '../../ButtonDeployModel'
import { rateBBoxModel, rateClassificationModel } from '../../../utils'
import { FaLightbulb } from 'react-icons/fa'
import { useDeleteModelMutation } from '../../../services/modelsApi'
import ConfirmDialog from '../../modals/ConfirmDialog'

const ModelContainer = styled.div<{
  $minimized?: boolean
  $indicatorColor?: string
}>`
  background: ${(props) => (props.$minimized ? '#292929' : '#1a1a1a')};
  border: 1px solid rgba(147, 51, 234, 0.2);
  border-radius: 8px;
  padding: 1rem;
  transition: all 0.2s ease;
  position: relative;

  ${(props) =>
    props.$indicatorColor &&
    `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 4px;
      height: 100%;
      background: ${props.$indicatorColor};
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px;
    }
  `}

  &:hover {
    border-color: rgba(147, 51, 234, 0.3);
    transform: translateY(-2px);
  }

  .model-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .model-info-container {
    background: #2d1b3b;
    border: 1px solid rgba(147, 51, 234, 0.2);
    border-radius: 8px;
    padding: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    .info-row {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .label {
        color: #8c8c8c;
        font-size: 0.9rem;
      }

      .value {
        color: #fff;
        font-size: 0.9rem;
      }
    }
  }

  .model-status-badge {
    align-self: flex-start;
    padding: 0.4rem 1rem;
    border-radius: 20px;
    font-weight: 500;
    font-size: 0.85rem;

    &.badge-initialized {
      background: #2c5282 !important;
      color: #fff !important;
      border: 1px solid #2b6cb0;
    }

    &.badge-training {
      background: #553c9a !important;
      color: #fff !important;
      border: 1px solid #6b46c1;
    }

    &.badge-stopping {
      background: rgb(154, 60, 65) !important;
      color: #fff !important;
      border: 1px solid rgba(154, 60, 65, 0.34);
    }

    &.badge-trained {
      background: #276749 !important;
      color: #fff !important;
      border: 1px solid #2f855a;
    }

    &.badge-deploying {
      background: #975a16 !important;
      color: #fff !important;
      border: 1px solid #b7791f;
    }

    &.badge-deployed {
      background: #285e61 !important;
      color: #fff !important;
      border: 1px solid #319795;
    }

    &.badge-danger {
      background: #9b2c2c !important;
      color: #fff !important;
      border: 1px solid #c53030;
    }
  }

  .deploy-button-container {
    margin-top: 0.5rem;
  }
`

const ModelNameRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #fff;
  font-size: 1.2rem;
  font-weight: 600;
`

const ModelHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`

const ModelTypeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #8c8c8c;
  font-size: 0.9rem;

  svg {
    width: 16px;
    height: 16px;
    stroke-width: 2px;
  }
`

const StarRatingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .star-rating {
    display: flex;
    gap: 0.25rem;
  }
`

const getModelStatusBadge = (model: Model) => {
  // Handle error states first
  if (model.state?.toLowerCase() === 'err' || model.deployment?.status?.toLowerCase() === 'err') {
    return (
      <Badge pill className="model-status-badge badge-danger">
        ERROR
      </Badge>
    )
  }

  // Handle deployment states
  if (model.state?.toLowerCase() === 'trained') {
    const deploymentStatus = model.deployment?.status?.toLowerCase()

    switch (deploymentStatus) {
      case 'creating':
        return (
          <Badge pill className="model-status-badge badge-deploying">
            Deploying
          </Badge>
        )
      case 'in_service':
        return (
          <Badge pill className="model-status-badge badge-deployed">
            Deployed
          </Badge>
        )
      case 'deleted':
      default:
        return (
          <Badge pill className="model-status-badge badge-trained">
            Trained
          </Badge>
        )
    }
  }

  // Handle training states
  switch (model.state?.toLowerCase()) {
    case 'initialized':
      return (
        <Badge pill className="model-status-badge badge-initialized">
          Initialized
        </Badge>
      )
    case 'training':
      return (
        <Badge pill className="model-status-badge badge-training">
          Training
        </Badge>
      )
    case 'stopping':
      return (
        <Badge pill className="model-status-badge badge-stopping">
          Stopping
        </Badge>
      )
    default:
      return null
  }
}

export interface ModelItemProps {
  model: Model
  project?: {
    annotation_type?: string
    id?: string
  }
  $minimized?: boolean
  showButtons?: boolean
  showDatasetSources?: boolean
  indicatorColor?: string
}

const ModelItem: React.FC<ModelItemProps> = ({
  model,
  project,
  $minimized = false,
  showButtons = true,
  showDatasetSources = true,
  indicatorColor,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteModel] = useDeleteModelMutation()
  const bBoxRating = (model: Model) => rateBBoxModel(model).bBoxRating
  const classificationRating = (model: Model) => rateClassificationModel(model).classificationRating

  const handleDelete = async () => {
    try {
      await deleteModel({ id: model.id }).unwrap()
    } catch (error) {
      console.error('Failed to delete model:', error)
    }
  }

  return (
    <ModelContainer $minimized={$minimized} $indicatorColor={indicatorColor}>
      <div className="model-content">
        <ModelNameRow>
          <Col xs={10}>{model.name}</Col>
          <Col xs={2} className="d-flex align-items-center justify-content-center">
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id={`tooltip-${model.id}`}>
                  {model.batch?.completed_content} Predictions available
                </Tooltip>
              }
            >
              <span>
                <FaLightbulb
                  size={12}
                  color={model.batch?.completed_content > 0 ? 'yellow' : 'gray'}
                />
              </span>
            </OverlayTrigger>
          </Col>
        </ModelNameRow>
        <ModelHeaderRow>
          <ModelTypeContainer>
            {project?.annotation_type === 'classification' ? (
              <>
                <Tag size={16} />
                <span>Classification</span>
              </>
            ) : (
              <>
                <Box size={16} />
                <span>Object Detection</span>
              </>
            )}
          </ModelTypeContainer>
          {model.state?.toLowerCase() === 'trained' && (
            <StarRatingContainer>
              {project?.annotation_type === 'classification' && (
                <div className="star-rating">
                  <StarRating rating={classificationRating(model)} small={true} />
                </div>
              )}
              {project?.annotation_type === 'bounding_box' && (
                <div className="star-rating">
                  <StarRating rating={bBoxRating(model)} small={true} />
                </div>
              )}
            </StarRatingContainer>
          )}
        </ModelHeaderRow>

        {!$minimized && (
          <>
            <div className="model-info-container">
              <div className="info-row">
                <span className="label">Created</span>
                <span className="value">{new Date(model.created_at).toLocaleDateString()}</span>
              </div>
              <div className="info-row">
                <span className="label">Status</span>
                <div>{getModelStatusBadge(model)}</div>
              </div>
            </div>
          </>
        )}

        <div className="deploy-button-container">
          <div className="d-flex gap-2">
            <ButtonDeployModel model={model} />
            {!$minimized && showButtons && (
              <Button
                disabled={model.deployment?.status?.toLowerCase() === 'creating'}
                variant="outline-danger"
                onClick={(e) => {
                  e.stopPropagation()
                  setShowDeleteConfirm(true)
                }}
                style={{
                  background: 'rgba(0, 0, 0, 0.2)',
                  border: '1px solid rgba(220, 53, 69, 0.5)',
                  color: '#fff',
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                  width: 'auto',
                  flex: '0 0 auto',
                }}
              >
                <Trash2 size={16} className="me-2" />
                Delete
              </Button>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        onConfirmRight={handleDelete}
        title="Delete Model"
        message={`Are you sure you want to delete the model "${model.name}"? This action cannot be undone.`}
        confirmRightText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </ModelContainer>
  )
}

export default ModelItem
